import { useQuery } from '@tanstack/react-query';
import { getMorrisonsReport, type MorrisonsPayload } from './report';
import { MR_EMPTY, MR_PREVIEW } from './fixture';

/*
 * Fetches the Morrisons report. When the call fails we fall back:
 *   - dev  → the design's sample figures, so the page can be reviewed
 *   - prod → zeros and "—", so a deployed build never shows invented figures
 * Either way `isPreview` is true and the header shows a "No live data" pill.
 *
 * DEV ONLY: `?preview` shows the design's figures even when the report loads,
 * and does not ask for the report at all.
 */
export function useMorrisonsReport(reportId: string, baseUrl: string): { data: MorrisonsPayload; isPreview: boolean; lastRefreshedAt: string | null } {
  const forced = import.meta.env.DEV && typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('preview');
  const query = useQuery({
    queryKey: ['morrisons-report', baseUrl, reportId],
    queryFn: () => getMorrisonsReport(reportId, baseUrl),
    enabled: !forced,
    retry: false,
    // The pipeline refreshes on a schedule; poll so an open page keeps up.
    refetchInterval: 5 * 60_000,
  });

  if (forced) return { data: MR_PREVIEW, isPreview: true, lastRefreshedAt: null };
  if (query.data) return { data: query.data.data, isPreview: false, lastRefreshedAt: query.data.meta?.last_refreshed_at ?? null };
  return { data: import.meta.env.DEV ? MR_PREVIEW : MR_EMPTY, isPreview: true, lastRefreshedAt: null };
}
